import React, { useState, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useUser } from 'src/utils/UserContext';
import { R2FThresholdTable } from '../../components/r2fThresholds/R2FThresholdTable';
import { useR2FThresholds } from '../../../core/hooks/useR2FThresholds';
import { AddHandlingTier } from '../../components/hierarchicalTable/AddHandlingTier';
import { AddProductionCapability } from '../../components/common/AddProductionCapability';
import { getValidWHTsForCountry } from '../../../types/WarehouseHandlingTier';
import { PendingThresholdChange } from 'src/features/types/r2fTypes';
import { useDefaultPCSafety } from '../../hooks/useDefaultPCSafety';
import { validateGSLAExistsForTRB } from '../../utils/defaultPCSafety';
import { useIsDefaultFCPCEnabled } from 'src/core/hooks/useIsDefaultFCPCEnabled';
import { getGSLADataForWarehouse } from 'src/core/hooks/useGranularSLA';
import SafetyWarning from '../../components/common/SafetyWarning';
import { useFCMetadata } from 'src/core/hooks/useGranularSLA';
import '../../components/r2fThresholds/styles.css';
import '../../components/common/styles.css';

interface LoadingProps {
    message: string;
}

// Placeholder for Loading component if it doesn't exist
const LoadingComponent: React.FC<LoadingProps> = ({ message }) => (
    <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>{message}</p>
    </div>
);

interface ErrorProps {
    message: string;
}

// Placeholder for ErrorMessage component if it doesn't exist
const ErrorMessageComponent: React.FC<ErrorProps> = ({ message }) => (
    <div className="error-container">
        <div className="error-icon">⚠️</div>
        <p>{message}</p>
    </div>
);

/**
 * R2F Threshold Manager Page Component
 * 
 * This page allows users to view and manage R2F threshold configurations
 * for different warehouse and production capabilities.
 */
const R2FThresholdManager: React.FC = () => {
    // Get site/warehouse ID from URL parameters - using the siteId param name from navigationRoutes.ts
    const { siteId = '' } = useParams<{ siteId: string }>();
    // Use the site ID as warehouse ID for consistency with the route parameter
    const warehouseId = siteId;
    
    // Let the API handle the authenticated user
    const { user } = useUser();
    const hasEditPermission = user?.isAuthorizedForSLAOverrides === true;
    const [selectedWarehouse, setSelectedWarehouse] = useState<string>(warehouseId || 'DEFAULT');
    
    // Fetch FC metadata for country-based WHT filtering
    const {
        data: fcMetadata,
        isLoading: isFCMetadataLoading,
    } = useFCMetadata(selectedWarehouse);
    
    // Compute available tiers based on country code
    const countryBasedTiers = getValidWHTsForCountry(fcMetadata?.countryCode, selectedWarehouse);
    
    // State for tracking pending changes
    const [pendingChanges, setPendingChanges] = useState<PendingThresholdChange[]>([]);
    
    // State to track custom PCs that need to be shown in the table
    const [pendingCustomPCs, setPendingCustomPCs] = useState<Set<string>>(new Set());
    
    // State for view-only mode - initialize to true if user doesn't have edit permissions
    const [viewOnlyMode, setViewOnlyMode] = useState<boolean>(!hasEditPermission);

    // State for inline validation error near save button
    const [inlineValidationError, setInlineValidationError] = useState<string>('');
    const [inlineValidationLink, setInlineValidationLink] = useState<{ href: string; label: string } | null>(null);

    // Initialize safety hook for Default_PC protection
    const safety = useDefaultPCSafety({
        operationType: 'R2F',
        userAlias: user?.login,
        warehouseId: selectedWarehouse
    });

    // PSSD detection for GSLA cross-validation
    const { isDefaultFCPCEnabled, isLoading: isPSSDLoading } = useIsDefaultFCPCEnabled(selectedWarehouse);
    const isPSSD = !isDefaultFCPCEnabled;

    // Fetch GSLA data for cross-validation on PSSD sites
    const {
        data: gslaData,
        isLoading: isGSLALoading,
        isError: isGSLAError,
        refetch: refetchGSLA,
    } = getGSLADataForWarehouse(selectedWarehouse, true, { enabled: isPSSD });

    // If user doesn't have SLA override permissions, force view-only mode
    useEffect(() => {
        if (!hasEditPermission) {
            setViewOnlyMode(true);
        }
    }, [hasEditPermission]);
    
    // Custom hook for R2F threshold data
    const { 
        data: originalData, 
        isLoading, 
        error, 
        saveThresholds, 
        userCanEdit: originalUserCanEdit,
        availableTiers,
        addProductionCapability: addPC,
        addCustomPC,
        deleteProductionCapability
    } = useR2FThresholds(
        selectedWarehouse, 
        user?.login, 
        viewOnlyMode ? false : true, // Force userCanEdit to false in view-only mode
        !viewOnlyMode // preserveDefaultTypes is false in view-only mode
    );
    
    // Explicitly calculate the effective userCanEdit value
    const userCanEdit = viewOnlyMode ? false : originalUserCanEdit;

    // PSSD: Detect TRB entries that lack corresponding GSLA entries on page load
    // Default/system PCs are excluded — they are managed at the network level, not per-site GSLA
    const GSLA_GAP_EXCLUDED_PCS = new Set(['Default_Network_PC', 'Default_FC_PC', 'STANDARD_PC', 'Default_PC']);

    const gslaGaps = useMemo(() => {
        if (!isPSSD || !originalData || isGSLALoading || isGSLAError) {
            return [];
        }

        const gslaLookup = new Set<string>(
            (gslaData?.publishedGSLAList || []).map(
                def => `${def.productionCapability}|${def.warehouseHandlingTier || 'Default'}`,
            ),
        );

        const missing: Array<{ productionCapability: string; handlingTier: string }> = [];
        originalData.productionCapabilities.forEach(pc => {
            if (GSLA_GAP_EXCLUDED_PCS.has(pc.name)) {
                return;
            }

            // Check default node
            if (pc.defaultNode) {
                const wht = pc.defaultNode.handlingTier || 'Default';
                if (!gslaLookup.has(`${pc.name}|${wht}`)) {
                    missing.push({ productionCapability: pc.name, handlingTier: wht });
                }
            }
            // Check additional nodes
            pc.additionalNodes.forEach(node => {
                if (!gslaLookup.has(`${pc.name}|${node.handlingTier}`)) {
                    missing.push({ productionCapability: pc.name, handlingTier: node.handlingTier });
                }
            });
        });

        return missing;
    }, [isPSSD, originalData, gslaData, isGSLALoading, isGSLAError]);

    const hasGSLAGaps = gslaGaps.length > 0;

    // When GSLA gaps exist on a PSSD site, force read-only to prevent any edits
    const effectiveUserCanEdit = hasGSLAGaps ? false : userCanEdit;

    // Function to toggle view-only mode
    const toggleViewMode = () => {
        setViewOnlyMode(prev => !prev);
    };
    
    // Enhance the data with pending custom PCs
    const data = useMemo(() => {
        if (!originalData || !pendingCustomPCs.size) return originalData;
        
        // Create a deep copy of the original data
        const enhancedData = {
            ...originalData,
            productionCapabilities: [...originalData.productionCapabilities]
        };
        
        // Add synthetic production capabilities for each pending custom PC
        Array.from(pendingCustomPCs).forEach(pcName => {
            // Check if this PC already exists in the data
            const existingPC = enhancedData.productionCapabilities.find(pc => pc.name === pcName);
            if (existingPC) return; // Skip if it already exists
            
            // Create a synthetic ID for this PC
            const syntheticId = `pending_${pcName.toLowerCase().replace(/\s+/g, '_')}`;
            
            // Find any pending changes for this PC to determine what tiers it has
            const pcChanges = pendingChanges.filter(change => 
                change.productionCapability === pcName && change.action === 'Add'
            );
            
            // Add this as a new production capability - note: we don't auto-create a default node
            enhancedData.productionCapabilities.push({
                id: syntheticId,
                name: pcName,
                defaultNode: undefined, // No default node initially
                additionalNodes: [], // Will be populated from pending changes in R2FThresholdTable
                isNetworkDefault: false,
                isFCDefault: false,
                hasDefaultNode: false // Explicitly mark that there's no default node
            });
            
            // Synthetic production capability added
        });
        
        return enhancedData;
    }, [originalData, pendingCustomPCs, pendingChanges]);

    // Handler for adding new custom Production Capabilities as pending changes
    const handleAddCustomPC = async (pcName: string, warehouseHandlingTier: string): Promise<void> => {
        // Proactive GSLA check at add-time for PSSD sites
        if (isPSSD) {
            let freshGSLA = gslaData;
            try {
                const result = await refetchGSLA();
                freshGSLA = result.data;
            }
            catch {
                // If refetch fails, continue with cached data
            }

            const probeChange: PendingThresholdChange = {
                productionCapability: pcName,
                handlingTier: warehouseHandlingTier === 'DEFAULT' ? 'Default' : warehouseHandlingTier,
                lowThreshold: 0,
                highThreshold: 0,
                action: 'Add',
                isDefault: warehouseHandlingTier === 'DEFAULT',
            };
            const gslaCheck = validateGSLAExistsForTRB([probeChange], freshGSLA, isPSSD, selectedWarehouse);
            if (gslaCheck.shouldBlock) {
                setInlineValidationError(
                    `⚠️ Warning: ${pcName} / ${warehouseHandlingTier} does not have a GSLA entry. You will not be able to save this threshold until GSLA is configured. Go to the SLA page: /sla/site/${selectedWarehouse}`,
                );
            }
            else {
                setInlineValidationError('');
            }
        }

        const newChanges: PendingThresholdChange[] = [];
        
        // Create a change for the specified handling tier
        const isDefault = warehouseHandlingTier === 'DEFAULT';
        
        const tierChange: PendingThresholdChange = {
            productionCapability: pcName,
            handlingTier: warehouseHandlingTier, // Use the exact tier name
            lowThreshold: 0,
            highThreshold: 100,
            userAlias: user?.login || '',
            reason: `Create ${warehouseHandlingTier} tier for PC ${pcName}`,
            action: 'Add',
            isDefault: isDefault
        };
        
        newChanges.push(tierChange);
        
        // Add all changes to the pending changes list
        setPendingChanges(prev => [...prev, ...newChanges]);
        
    // Add to the set of pending custom PCs
    setPendingCustomPCs(prev => new Set([...Array.from(prev), pcName]));
};

// Handler for removing pending (unsaved) PCs
const handleRemovePendingPC = (pcName: string) => {
    // Remove from pendingCustomPCs
    setPendingCustomPCs(prev => {
        const newSet = new Set(prev);
        newSet.delete(pcName);
        return newSet;
    });
    
    // Remove all pending changes for this PC
    setPendingChanges(prev => prev.filter(change => change.productionCapability !== pcName));
};
    
    // Handle saving all changes, including custom PCs and other modifications
    const handleSaveAll = async (tableChanges: PendingThresholdChange[]) => {
        // Combine the custom PC changes with any table changes
        const allChanges = [...pendingChanges, ...tableChanges];
        
        // CRITICAL SAFETY CHECK: Validate for Default_PC entries
        const safetyCheck = safety.validateSafety(allChanges);
        
        if (safetyCheck.shouldBlock) {
            // Block the save operation and show error message
            safety.showWarning(safetyCheck.errorMessage, 'error');
            
            // Log the safety violation for audit purposes
            safety.logViolation(safetyCheck.defaultPCCount);
            
            // Throw to signal table to preserve pending rows on validation failure
            throw new Error(safetyCheck.errorMessage);
        }

        // PSSD VALIDATION: Refetch GSLA data to ensure freshness, then validate
        if (isPSSD) {
            let freshGSLAData = gslaData;
            try {
                const refetchResult = await refetchGSLA();
                freshGSLAData = refetchResult.data;
            }
            catch {
                const msg = 'GSLA cross-validation is unavailable. TRB saves are blocked for PSSD sites until GSLA data loads successfully.';
                setInlineValidationError(msg);
                setInlineValidationLink(null);
                throw new Error(msg);
            }

            const gslaCheck = validateGSLAExistsForTRB(allChanges, freshGSLAData, isPSSD, selectedWarehouse);
            if (gslaCheck.shouldBlock) {
                setInlineValidationError(gslaCheck.errorMessage);
                setInlineValidationLink(gslaCheck.slaLink || null);
                throw new Error(gslaCheck.errorMessage);
            }
        }

        // Clear any previous inline validation error on successful validation
        setInlineValidationError('');

        // Call the API with all changes (already validated to not contain Default_PC)
        await saveThresholds(allChanges);
        
        // Clear the pending changes and custom PCs after successful save
        setPendingChanges([]);
        setPendingCustomPCs(new Set());
    };
    
    // Display loading state while fetching data (include GSLA for PSSD sites)
    if (isLoading || isFCMetadataLoading || isPSSDLoading || (isPSSD && isGSLALoading)) {
        return <LoadingComponent message="Loading R2F threshold configurations..." />;
    }
    
    // Display error if data fetching failed
    if (error) {
        return <ErrorMessageComponent message={error} />;
    }
    
    // Get available handling tiers to use as production capabilities
    const handleAddProductionCapability = async (pcId: string, handlingTier: string) => {
        // Instead of making an immediate API call, add as a pending change
        try {
            // Create pending changes for the new PC with default thresholds
            const newChanges: PendingThresholdChange[] = [{
                productionCapability: handlingTier, // Using the handling tier as the PC name
                handlingTier: 'DEFAULT',
                lowThreshold: 0,
                highThreshold: 100,
                userAlias: user?.login || '',
                reason: `Create new production capability ${handlingTier}`,
                action: 'Add',
                isDefault: true
            }];
            
            // Add to pending changes
            setPendingChanges(prev => [...prev, ...newChanges]);
            
            // Add to pending custom PCs to ensure it shows in the UI
            setPendingCustomPCs(prev => new Set([...Array.from(prev), handlingTier]));
            
            return true;
        } catch (err) {
            console.error('Failed to add production capability thresholds:', err);
            return false;
        }
    };
    
    // Display empty state if no data is available
    if (!data || data.productionCapabilities.length === 0) {
        return (
            <div className="r2f-threshold-manager">
                <h1>TRB Threshold Configuration</h1>
                <div className="empty-state">
                    <p>No TRB threshold configurations found for this warehouse.</p>
                    {effectiveUserCanEdit && (
                        <div className="r2f-add-production-capability">
                            <h3>Select Production Capability</h3>
                            <p>Add TRB threshold at warehouse handling tier for production capability:</p>
                            <table className="r2f-add-table">
                                <tbody>
                                    <AddHandlingTier
                                        pcId="production-capabilities" // placeholder ID since we don't have production capabilities here
                                        availableTiers={countryBasedTiers}
                                        onAdd={handleAddProductionCapability}
                                        userCanEdit={effectiveUserCanEdit}
                                    />
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        );
    }
    
    return (
        <div className="r2f-threshold-manager">
            <h1>TRB Threshold Configuration</h1>
            
            {/* Safety Warning Display */}
            {safety.warningState?.isVisible && (
                <SafetyWarning
                    message={safety.warningState.message}
                    type={safety.warningState.type}
                    operationType={safety.warningState.operationType}
                    onDismiss={safety.dismissWarning}
                />
            )}

            {/* PSSD: Red banner when TRB entries exist without corresponding GSLA */}

            <div className="r2f-description">
                <p>
                    Configure low and high Tiny-Red-Button (TRB) thresholds for different production capabilities and nodes.
                    These thresholds determine when inventory is considered ready for fulfillment.
                </p>
                <p className="inheritance-note">
                    <strong>Note:</strong> Values shown represent the effective thresholds for this warehouse. 
                    Some values may be inherited from network-wide default settings when not explicitly configured for this warehouse.
                </p>
            </div>

            {hasGSLAGaps && (
                <div className="pssd-inline-error banner">
                    <div>
                        <strong>❌ Editing disabled:</strong> The following TRB threshold entries do not have corresponding GSLA values configured.
                        All editing is blocked until the missing GSLA entries are added on the{' '}
                        <a href={`/sla/site/${selectedWarehouse}`}>SLA page</a>.
                        <ul>
                            {gslaGaps.map((gap, i) => (
                                <li key={i}>{gap.productionCapability} / {gap.handlingTier}</li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            {/* Only show toggle if user has edit permissions */}
            {hasEditPermission && (
                <div className="view-mode-toggle">
                    <label className="toggle-switch">
                        <input 
                            type="checkbox" 
                            checked={viewOnlyMode} 
                            onChange={toggleViewMode} 
                        />
                        <span className="toggle-label">View-Only Mode</span>
                        {viewOnlyMode && <span className="toggle-note">(Default types hidden)</span>}
                    </label>
                </div>
            )}
            
            <div className="warehouse-selector">
                <h3>
                    Warehouse: {selectedWarehouse === 'DEFAULT' 
                        ? 'DEFAULT (Network-Wide Settings)' 
                        : selectedWarehouse}
                </h3>
                {/* Warehouse selector would go here in a real implementation */}
            </div>

            {effectiveUserCanEdit && (
                <>
                    <div className="add-pc-section">
                        <h3>Add Override for Additional Production Capability</h3>
                        <AddProductionCapability
                            existingPCs={data.productionCapabilities.map(pc => pc.name)}
                            onAdd={handleAddCustomPC}
                            userCanEdit={effectiveUserCanEdit}
                            availableTiers={countryBasedTiers}
                            
                            // New API integration props
                            warehouseIds={[selectedWarehouse]}
                            enableAPIMode={true}
                            fallbackToTextInput={true}
                            showErrorDetails={false}
                            
                            // UI customization for R2F context
                            dropdownPlaceholder="Select a Production Capability for R2F thresholds"
                            textInputPlaceholder="Enter custom Production Capability name"
                            loadingMessage="Loading production capabilities..."
                            
                            // Event callbacks for better user experience
                            onAPIError={(error) => {
                                console.warn('Failed to load production capabilities from API for R2F:', error.message);
                            }}
                            onModeSwitch={(mode) => {
                                console.log(`R2F AddProductionCapability switched to ${mode} mode`);
                            }}
                        />
                    </div>
                    <hr className="divider" />
                </>
            )}
            
            <R2FThresholdTable
                data={data}
                onSave={handleSaveAll}
                onDelete={(productionCapabilityId, productionCapability, reason, actionSource) => {
                    // Process deletion request
                    return deleteProductionCapability(productionCapability, reason, actionSource);
                }}
                onRemovePendingPC={handleRemovePendingPC}
                userCanEdit={effectiveUserCanEdit}
                availableTiers={countryBasedTiers}
                pendingChanges={pendingChanges}
                warehouseId={selectedWarehouse}
                isPSSD={isPSSD}
            />
            
            {pendingChanges.length > 0 && (
                <div className="pending-changes-info">
                    <p>You have {pendingChanges.length} pending change(s) that will be applied when you click Save.</p>
                </div>
            )}

            {/* Inline validation error near save area */}
            {inlineValidationError && (
                <div className="pssd-inline-error">
                    <span>
                        {inlineValidationError}
                        {inlineValidationLink && (
                            <a href={inlineValidationLink.href}>{inlineValidationLink.label}</a>
                        )}
                    </span>
                    <button
                        className="dismiss-button"
                        onClick={() => { setInlineValidationError(''); setInlineValidationLink(null); }}
                    >
                        ✕
                    </button>
                </div>
            )}
        </div>
    );
};

export default R2FThresholdManager;
